import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================
// Photo slider — adapted from parent AmenitiesList
// ============================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback(
    (dir: 1 | -1) => {
      setCurrent(prev => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="fa-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`fa-slider-img${i === current ? ' fa-slider-img--active' : ''}`}
          priority={i === 0}
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="fa-slider-arrow fa-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            className="fa-slider-arrow fa-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <div className="fa-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`fa-slider-dot${i === current ? ' fa-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// Amenity column — photo slider + paginated list
// ============================================================

const PAGE_SIZE = 10;

interface AmenityColumnProps {
  headingNode: React.ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityColumn({ headingNode, headingText, items, photos }: AmenityColumnProps) {
  const [page, setPage] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasPagination = totalPages > 1;

  const goToPage = useCallback(
    (next: number) => {
      if (next === page || transitioning) return;
      setTransitioning(true);
      setTimeout(() => {
        setPage(next);
        setTimeout(() => setTransitioning(false), 30);
      }, 250);
    },
    [page, transitioning],
  );

  return (
    <div className="fa-col">
      {photos.length > 0 && <PhotoSlider photos={photos} heading={headingText} />}

      {headingNode}

      <ul className={`fa-col-list fa-page-transition${transitioning ? ' fa-page-out' : ''}`}>
        {pageItems.map((item, i) => (
          <li key={`${page}-${i}`} className="fa-col-item">
            <span className="fa-col-bullet" aria-hidden="true" />
            <span>{item.title || ''}</span>
          </li>
        ))}
      </ul>

      {hasPagination && (
        <div className="fa-pagination">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 0 || transitioning}
            className="fa-page-btn"
            aria-label="Previous page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="fa-page-dots">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                disabled={transitioning}
                className={`fa-page-dot${i === page ? ' fa-page-dot--active' : ''}`}
                aria-label={`Page ${i + 1}`}
                aria-current={i === page ? 'page' : undefined}
              />
            ))}
          </div>

          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages - 1 || transitioning}
            className="fa-page-btn"
            aria-label="Next page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Blurb panel — layout-only shell; content inlined in main
// ============================================================

function BlurbPanel({ children }: { children: React.ReactNode }) {
  return <div className="fa-blurb">{children}</div>;
}

// ============================================================
// Main component
// ============================================================

export function FeaturedAmenities() {
  const communityHeading = useContentToken('featuredamenities.communityHeading');
  const apartmentHeading = useContentToken('featuredamenities.apartmentHeading');

  const communityItems = useCollectionBinding<Amenity>('featuredamenities.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('featuredamenities.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('featuredamenities.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('featuredamenities.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  const communityRef = useScrollReveal<HTMLDivElement>();
  const apartmentRef = useScrollReveal<HTMLDivElement>();

  useRevealStyles();

  const hasCommunity = communityItems.length > 0;
  const hasApartment = apartmentItems.length > 0;

  if (!hasCommunity && !hasApartment) {
    return (
      <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-12 text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Amenities will be displayed here.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 lg:space-y-12">

        {/* Row 1 (lg): AmenityColumn | Blurb */}
        {hasCommunity && (
          <div ref={communityRef} className="reveal-fade-up fa-row">
            <AmenityColumn
              headingNode={
                <EditableToken
                  path="featuredamenities.communityHeading"
                  as="h3"
                  className="fa-col-heading"
                >
                  {communityHeading || 'Community Amenities'}
                </EditableToken>
              }
              headingText={communityHeading || 'Community Amenities'}
              items={communityItems}
              photos={communityPhotos}
            />
            <BlurbPanel>
              <RichTextContent path="featuredamenities.communityBlurbTitle" as="h2" className="fa-blurb-title" />
              <RichTextContent path="featuredamenities.communityBlurbSubtitle" as="p" className="fa-blurb-subtitle" />
              <div className="fa-blurb-divider" aria-hidden="true" />
              <RichTextContent path="featuredamenities.communityBlurbText" as="p" className="fa-blurb-text" />
            </BlurbPanel>
          </div>
        )}

        {/* Row 2 (lg): Blurb | AmenityColumn */}
        {hasApartment && (
          <div ref={apartmentRef} className="reveal-fade-up fa-row">
            <BlurbPanel>
              <RichTextContent path="featuredamenities.apartmentBlurbTitle" as="h2" className="fa-blurb-title" />
              <RichTextContent path="featuredamenities.apartmentBlurbSubtitle" as="p" className="fa-blurb-subtitle" />
              <div className="fa-blurb-divider" aria-hidden="true" />
              <RichTextContent path="featuredamenities.apartmentBlurbText" as="p" className="fa-blurb-text" />
            </BlurbPanel>
            <AmenityColumn
              headingNode={
                <EditableToken
                  path="featuredamenities.apartmentHeading"
                  as="h3"
                  className="fa-col-heading"
                >
                  {apartmentHeading || 'Apartment Features'}
                </EditableToken>
              }
              headingText={apartmentHeading || 'Apartment Features'}
              items={apartmentItems}
              photos={apartmentPhotos}
            />
          </div>
        )}
      </div>

      <style>{`
        /* ---- Two-column alternating rows ---- */
        .fa-row {
          display: grid;
          grid-template-columns: 1fr;
          align-items: stretch;
        }
        @media (min-width: 1024px) {
          .fa-row {
            grid-template-columns: 1fr 1fr;
          }
        }

        /* ---- Amenity column ---- */
        .fa-col {
          background: var(--vibe-color-surface, white);
          border: 1px solid var(--vibe-color-border);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* ---- Blurb panel ---- */
        .fa-blurb {
          background: var(--vibe-color-primary);
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 3rem 2.5rem;
          min-height: 300px;
        }

        .fa-blurb-title {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 3vw, 2.5rem);
          font-weight: 700;
          line-height: 1.15;
          margin: 0 0 0.75rem;
          color: inherit;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .fa-blurb-subtitle {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 400;
          margin: 0 0 1.25rem;
          color: rgba(255, 255, 255, 0.75);
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .fa-blurb-divider {
          width: 48px;
          height: 2px;
          background: rgba(255, 255, 255, 0.4);
          margin: 0 0 1.5rem;
          flex-shrink: 0;
        }

        .fa-blurb-text {
          font-family: var(--vibe-font-body);
          font-size: 0.975rem;
          line-height: 1.8;
          color: rgba(255, 255, 255, 0.88);
          margin: 0;
          max-width: 42ch;
        }

        /* ---- Photo slider ---- */
        .fa-slider {
          position: relative;
          width: 100%;
          height: 280px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .fa-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .fa-slider-img--active { opacity: 1; }

        .fa-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .fa-slider:hover .fa-slider-arrow { opacity: 1; }
        .fa-slider-arrow:hover { background: rgba(0, 0, 0, 0.55); }
        .fa-slider-arrow--prev { left: 10px; }
        .fa-slider-arrow--next { right: 10px; }

        .fa-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .fa-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .fa-slider-dot:hover { transform: scale(1.25); border-color: white; }
        .fa-slider-dot--active { background: white; border-color: white; }

        @media (prefers-reduced-motion: reduce) {
          .fa-slider-img,
          .fa-page-transition { transition: none; }
        }

        /* ---- Column heading ---- */
        .fa-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 400;
          color: var(--vibe-color-text);
          text-align: center;
          padding: 1.25rem 1.5rem 0;
          margin: 0;
          letter-spacing: 0.02em;
        }

        /* ---- Amenity list ---- */
        .fa-col-list {
          list-style: none;
          padding: 1rem 1.75rem 1.5rem;
          margin: 0;
          flex: 1;
        }
        .fa-col-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.3rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text);
          line-height: 1.5;
        }
        .fa-col-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        /* ---- Page transition ---- */
        .fa-page-transition {
          transition: opacity 0.25s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
          opacity: 1;
          transform: translateY(0);
        }
        .fa-page-out { opacity: 0; transform: translateY(8px); }

        /* ---- Pagination ---- */
        .fa-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem 1.25rem;
        }
        .fa-page-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s, background 0.2s;
        }
        .fa-page-btn:hover:not(:disabled) {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
        .fa-page-btn:disabled { opacity: 0.3; cursor: default; }
        .fa-page-dots { display: flex; align-items: center; gap: 6px; }
        .fa-page-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-border);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .fa-page-dot:hover {
          border-color: var(--vibe-color-primary);
          transform: scale(1.2);
        }
        .fa-page-dot--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeaturedAmenities',
  displayName: 'Featured Amenities',
  tokens: {
    // ---- Column headings (static labels, no generator) ----
    'featuredamenities.communityHeading': {
      label: 'Community Column Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'featuredamenities.apartmentHeading': {
      label: 'Apartment Column Heading',
      type: 'text',
      defaultValue: 'Apartment Features',
    },

    // ---- Community blurb panel (all generated) ----
    'featuredamenities.communityBlurbTitle': {
      label: 'Community Blurb Title',
      type: 'richtext',
      generator: {
        prompt: 'Short heading for the community amenities blurb panel.',
        context: 'Full-height panel beside the community amenities photo slider and list. Should feel aspirational and distinct from the column heading.',
        length: '3–6 words',
      },
    },
    'featuredamenities.communityBlurbSubtitle': {
      label: 'Community Blurb Subtitle',
      type: 'richtext',
      generator: {
        prompt: 'One-line subtitle for the community amenities blurb panel.',
        context: 'Appears beneath the blurb title, above the body text.',
        length: '4–8 words',
      },
    },
    'featuredamenities.communityBlurbText': {
      label: 'Community Blurb Text',
      type: 'richtext',
      generator: {
        prompt: 'Body copy for the community amenities blurb panel. Highlight the lifestyle benefit of the shared spaces.',
        context: 'Sits beside the community amenities list. Should add texture and aspiration, not just restate the amenity names.',
        length: '2–3 sentences',
      },
    },

    // ---- Apartment blurb panel (all generated) ----
    'featuredamenities.apartmentBlurbTitle': {
      label: 'Apartment Blurb Title',
      type: 'richtext',
      generator: {
        prompt: 'Short heading for the apartment features blurb panel.',
        context: 'Full-height panel beside the apartment features photo slider and list. Should feel personal and elevated.',
        length: '3–6 words',
      },
    },
    'featuredamenities.apartmentBlurbSubtitle': {
      label: 'Apartment Blurb Subtitle',
      type: 'richtext',
      generator: {
        prompt: 'One-line subtitle for the apartment features blurb panel.',
        context: 'Appears beneath the blurb title, above the body text.',
        length: '4–8 words',
      },
    },
    'featuredamenities.apartmentBlurbText': {
      label: 'Apartment Blurb Text',
      type: 'richtext',
      generator: {
        prompt: 'Body copy for the apartment features blurb panel. Highlight the quality and thoughtfulness of the in-unit features.',
        context: 'Sits beside the apartment features list. Should feel warm and personal without restating feature names.',
        length: '2–3 sentences',
      },
    },
  },
  collectionBindings: {
    'featuredamenities.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'featuredamenities.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'featuredamenities.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'featuredamenities.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(FeaturedAmenities as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FeaturedAmenities;
